import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/axios";
import Modal from "../../../components/common/Modal";
import formatError from "../../../utils/formatError";
import { useVoicePage } from "../../../hooks/useVoicePage";
import VoicePageMic from "../../../components/common/VoicePageMic";

export default function Toss() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [match, setMatch] = useState(null);
  const [teams, setTeams] = useState([]);

  const [wonBy, setWonBy] = useState("");
  const [decision, setDecision] = useState("");

  // Pre-match conditions
  const [pitchReport, setPitchReport] = useState("");
  const [pitchDryness, setPitchDryness] = useState("");
  const [pitchGrass, setPitchGrass] = useState("");
  const [pitchCracks, setPitchCracks] = useState(false);
  const [dewExpected, setDewExpected] = useState(false);
  const [weatherTempC, setWeatherTempC] = useState("");
  const [weatherWindKph, setWeatherWindKph] = useState("");
  const [weatherHumidity, setWeatherHumidity] = useState("");

  const [saving, setSaving] = useState(false);
  const [showReadyModal, setShowReadyModal] = useState(false);
  const [updatingState, setUpdatingState] = useState(false);
  const [error, setError] = useState("");

  /* =========================
     Fetch match details
  ========================= */
  useEffect(() => {
    api
      .get(`/api/matches/matches/${id}/detail/`)
      .then((res) => {
        const data = res.data;
        setMatch(data);
        setTeams([
          { id: data.team1_id, name: data.team1_name },
          { id: data.team2_id, name: data.team2_name },
        ]);

        // Match-state policy:
        //   DRAFT       → safe to mark READY then record toss (modal flow).
        //   READY       → toss form usable as-is, no state change needed.
        //   IN_PROGRESS → match is already being scored; redirect away
        //                 instead of letting the operator revert state.
        //   COMPLETED   → match done; redirect to scorecard.
        if (data.state === "IN_PROGRESS") {
          alert("Match already started — redirecting to scoring.");
          navigate(`/ball/scoring/${id}`);
          return;
        }
        if (data.state === "COMPLETED") {
          alert("Match is complete — redirecting to scorecard.");
          navigate(`/cricket/matches/${id}/scorecard`);
          return;
        }
        if (data.state === "DRAFT") {
          setShowReadyModal(true);
        }
      })
      .catch((err) => {
        alert("Failed to load match");
        console.warn("toss failure:", err);
        navigate("/cricket/matches");
      });
  }, [id, navigate]);

  /* =========================
     Voice commands

     Founder ask 2026-05-26: "Voice for toss/squad/XI/player creation."
     Manual Next buttons stay — voice fills the fields, operator
     verifies and clicks Save themselves. No auto-advance, per ask
     ("auto advance will not give them time to edit if anything is
     wrong").
  ========================= */
  const tossParser = useCallback((raw) => {
    const t = raw.toLowerCase().replace(/[.,!?]+$/g, "").trim();
    if (!t) return null;
    const labels = [];

    // ── Team match — look for any team name (or its last token) in the
    // utterance. Run before the decision parse so "RCB won, bat first"
    // and "RCB bat" both wire the team.
    let pickedTeam = null;
    for (const team of teams) {
      const name = (team.name || "").toLowerCase();
      if (!name) continue;
      const lastTok = name.split(/\s+/).pop();
      if (t.includes(name) || (lastTok && lastTok.length > 2 && new RegExp(`\\b${lastTok}\\b`).test(t))) {
        pickedTeam = team;
        break;
      }
    }
    if (pickedTeam) {
      setWonBy(pickedTeam.id);
      labels.push(`won by ${pickedTeam.name}`);
    }

    // ── Decision — bat or bowl, with all the cricket idioms.
    if (/\b(bat first|batting first|chose to bat|elected to bat|will bat|bat\b)/.test(t)) {
      setDecision("bat");
      labels.push("chose to bat");
    } else if (/\b(bowl first|bowling first|chose to bowl|elected to bowl|will bowl|field first|fielding|bowl\b)/.test(t)) {
      setDecision("bowl");
      labels.push("chose to bowl");
    }

    // ── Pitch report ─────────────────────────────────────────────────
    // Founder ask 2026-05-26: voice-fill the pitch + weather fields
    // so an operator can rattle them off without tapping each picker.
    // Phrasings: "pitch dry green cracks dew expected temperature 28
    // humidity 60 wind 15".
    if (/\b(dry pitch|pitch dry|dry track|dusty)\b/.test(t)) {
      setPitchDryness("dry"); labels.push("pitch dry");
    } else if (/\b(damp pitch|pitch damp|wet pitch|pitch wet|moist)\b/.test(t)) {
      setPitchDryness("damp"); labels.push("pitch damp");
    } else if (/\b(normal pitch|pitch normal)\b/.test(t)) {
      setPitchDryness("normal"); labels.push("pitch normal");
    }
    if (/\b(green pitch|green top|grassy|good covering)\b/.test(t)) {
      setPitchGrass("green"); labels.push("grass green");
    } else if (/\b(bald|patchy grass|no grass|bare pitch)\b/.test(t)) {
      setPitchGrass("bald"); labels.push("grass bald");
    } else if (/\b(some grass|light grass|thin covering)\b/.test(t)) {
      setPitchGrass("some"); labels.push("grass some");
    }
    if (/\b(cracks|cracked|cracked pitch)\b/.test(t)) {
      setPitchCracks(true); labels.push("cracks");
    } else if (/\b(no cracks|crack free)\b/.test(t)) {
      setPitchCracks(false);
    }
    if (/\b(dew expected|dew likely|expecting dew|dew factor)\b/.test(t)) {
      setDewExpected(true); labels.push("dew expected");
    } else if (/\b(no dew|dry conditions)\b/.test(t)) {
      setDewExpected(false);
    }
    // Temperature, wind, humidity — number + unit-optional phrasing.
    const tempMatch = t.match(/\b(\d{1,2})\s*(?:degrees?|°|c|celsius)\b|temperature\s+(\d{1,2})/);
    if (tempMatch) {
      const n = parseInt(tempMatch[1] || tempMatch[2], 10);
      if (n >= 0 && n <= 55) { setWeatherTempC(String(n)); labels.push(`${n}°C`); }
    }
    const windMatch = t.match(/wind\s+(\d{1,3})|(\d{1,3})\s*(?:kph|km\/h|kmh)\b/);
    if (windMatch) {
      const n = parseInt(windMatch[1] || windMatch[2], 10);
      if (n >= 0 && n <= 200) { setWeatherWindKph(String(n)); labels.push(`wind ${n}`); }
    }
    const humidMatch = t.match(/humidity\s+(\d{1,3})|(\d{1,3})\s*(?:percent|%)/);
    if (humidMatch) {
      const n = parseInt(humidMatch[1] || humidMatch[2], 10);
      if (n >= 0 && n <= 100) { setWeatherHumidity(String(n)); labels.push(`humidity ${n}%`); }
    }
    // Free-form pitch report — anything starting with "pitch report" /
    // "note" / "comment" goes verbatim into pitchReport.
    const reportMatch = raw.match(/(?:pitch report|pitch note|note|comment|remark)[: ]+(.+)$/i);
    if (reportMatch && reportMatch[1].length > 2) {
      setPitchReport(reportMatch[1].trim());
      labels.push(`note: ${reportMatch[1].slice(0, 30)}`);
    }

    // ── Save shortcut.
    if (/^save( toss)?\s*$/.test(t) || /\bsave (the )?toss\b/.test(t)) {
      // Defer to the existing button handler — kept in sync with the
      // canSave gate (state must be READY, both fields filled).
      // Use setTimeout so the state updates above flush first.
      setTimeout(() => {
        if (wonBy || pickedTeam) {
          document.getElementById("save-toss-btn")?.click();
        }
      }, 0);
      labels.push("save");
    }

    return labels.length ? labels.join(" + ") : null;
  }, [teams, wonBy]);

  const voice = useVoicePage({ parser: tossParser });

  /* =========================
     Save Toss
  ========================= */
  const saveToss = async () => {
    if (!wonBy || !decision) return;

    setSaving(true);
    let pitchSaveFailed = false;
    try {
      // Save toss
      await api.post(`/api/matches/matches/${id}/toss/`, {
        won_by: wonBy,
        decision,
      });

      // Save pitch + weather + report on the Match record. We treat
      // this as non-blocking (toss succeeded), but if it fails we now
      // *surface* the error instead of silently swallowing it. The
      // operator can then re-edit the match record from MatchControl
      // / EditMatch instead of discovering hours later that pitch
      // data wasn't recorded.
      const pitch_conditions = {
        dryness: pitchDryness, grass: pitchGrass,
        cracks: pitchCracks, dew_expected: dewExpected,
      };
      try {
        await api.put(`/api/matches/matches/${id}/`, {
          pitch_report: pitchReport,
          pitch_conditions,
        });
      } catch (pitchErr) {
        console.error("Pitch save failed:", pitchErr);
        pitchSaveFailed = true;
      }

      if (pitchSaveFailed) {
        setError(
          "Toss saved, but pitch/weather details didn't save. You can " +
          "re-enter them on the Edit Match page after Playing XI is set. " +
          "Continuing to XI…"
        );
        // Still navigate after a short pause so the operator sees the warning.
        setTimeout(() => navigate(`/cricket/matches/${id}/playing-xi`), 2200);
        return;
      }
      navigate(`/cricket/matches/${id}/playing-xi`);
    } catch (err) {
      setError(formatError(err, "Failed to save toss"));
    } finally {
      setSaving(false);
    }
  };

  if (!match) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-button-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading match details...</p>
        </div>
      </div>
    );
  }

  const isFormValid = wonBy && decision;
  const canSave = isFormValid && match.state === "READY" && !saving;

  return (
    <div className="min-h-screen bg-bg-main py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/cricket/matches")}
            className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors mb-4"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Matches
          </button>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
            <h1 className="text-3xl font-bold text-text-primary">
              Match Toss
            </h1>
            <VoicePageMic
              voice={voice}
              helpTitle="Toss — voice commands"
              examples={[
                `${teams[0]?.name || "Team A"} won toss chose to bat`,
                `${teams[1]?.name || "Team B"} won bowl first`,
                "chose to bat",
                "chose to bowl",
                "save toss",
              ]}
              helpBody={
                <>
                  Say <em>team name + won + bat/bowl</em> in one phrase
                  (e.g. <em>"India won chose to bat"</em>) or split it
                  across two — voice keeps both fields. The Next /
                  Save button stays manual so you can verify before
                  committing.
                </>
              }
            />
          </div>
          <p className="text-text-secondary">
            Record the toss result for this match
          </p>
        </div>

        {/* Match Info Card */}
        <div className="bg-bg-primary rounded-xl p-6 mb-6 border border-bg-secondary shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center border border-emerald-500/30">
              <svg
                className="w-6 h-6 text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <div>
              <p className="text-text-secondary text-sm">Match Status</p>
              <p className="text-text-primary text-lg font-semibold">
                {match.state}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 text-center">
            <div className="flex-1 p-4 bg-bg-secondary rounded-lg border border-bg-main">
              <p className="text-text-secondary text-sm mb-1">Team 1</p>
              <p className="text-text-primary text-lg font-semibold">
                {teams[0]?.name}
              </p>
            </div>
            <div className="text-text-secondary text-2xl">vs</div>
            <div className="flex-1 p-4 bg-bg-secondary rounded-lg border border-bg-main">
              <p className="text-text-secondary text-sm mb-1">Team 2</p>
              <p className="text-text-primary text-lg font-semibold">
                {teams[1]?.name}
              </p>
            </div>
          </div>
        </div>

        {/* Toss Selection Card */}
        <div className="bg-bg-primary rounded-xl overflow-hidden border border-bg-secondary shadow-lg">
          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 px-6 py-4 border-b border-purple-500/20">
            <h2 className="text-xl font-semibold text-text-primary flex items-center gap-2">
              <svg
                className="w-6 h-6 text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Toss Details
            </h2>
          </div>

          <div className="p-4 sm:p-8">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* Toss Winner */}
              <div>
                <label className="block text-text-secondary text-sm font-medium mb-3">
                  Toss Won By <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    value={wonBy}
                    onChange={(e) => setWonBy(e.target.value)}
                    className="w-full p-4 pr-10 rounded-lg bg-bg-secondary text-text-primary border-2 border-bg-main focus:border-purple-500/50 outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="">-- Select Team --</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
                {wonBy && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-purple-400">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {teams.find((t) => t.id === wonBy)?.name} selected
                  </div>
                )}
              </div>

              {/* Decision */}
              <div>
                <label className="block text-text-secondary text-sm font-medium mb-3">
                  Decision <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    value={decision}
                    onChange={(e) => setDecision(e.target.value)}
                    className="w-full p-4 pr-10 rounded-lg bg-bg-secondary text-text-primary border-2 border-bg-main focus:border-blue-500/50 outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="">-- Select Decision --</option>
                    <option value="BAT">Bat First</option>
                    <option value="BOWL">Bowl First</option>
                  </select>
                  <svg
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
                {decision && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-blue-400">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Elected to {decision === "BAT" ? "bat" : "bowl"} first
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            {isFormValid && (
              <div className="mt-8 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-emerald-400 font-medium mb-1">
                      Toss Summary
                    </p>
                    <p className="text-text-primary text-sm">
                      <span className="font-semibold">
                        {teams.find((t) => t.id === wonBy)?.name}
                      </span>{" "}
                      won the toss and elected to{" "}
                      <span className="font-semibold">
                        {decision === "BAT" ? "bat" : "bowl"} first
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pitch & weather block — Phase C */}
          <div className="bg-bg-primary rounded-xl p-6 mb-6 border border-bg-secondary shadow-lg">
            <h3 className="text-text-primary font-semibold mb-3">Pitch &amp; weather (optional)</h3>
            <p className="text-text-secondary text-xs mb-4">
              Pre-match conditions feed into swing/spin/bounce analytics later.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Pitch dryness</span>
                <select value={pitchDryness} onChange={(e) => setPitchDryness(e.target.value)}
                  className="bg-bg-secondary text-text-primary px-3 py-2 rounded">
                  <option value="">—</option>
                  <option value="DRY">Dry</option>
                  <option value="DAMP">Damp</option>
                  <option value="GREEN">Green / wet</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Grass cover</span>
                <select value={pitchGrass} onChange={(e) => setPitchGrass(e.target.value)}
                  className="bg-bg-secondary text-text-primary px-3 py-2 rounded">
                  <option value="">—</option>
                  <option value="BARE">Bare</option>
                  <option value="MIXED">Mixed</option>
                  <option value="GRASSY">Grassy</option>
                </select>
              </label>
              <div className="flex items-end gap-3">
                <label className="flex items-center gap-2 text-text-primary">
                  <input type="checkbox" checked={pitchCracks} onChange={(e) => setPitchCracks(e.target.checked)} />
                  <span className="text-xs">Visible cracks</span>
                </label>
                <label className="flex items-center gap-2 text-text-primary">
                  <input type="checkbox" checked={dewExpected} onChange={(e) => setDewExpected(e.target.checked)} />
                  <span className="text-xs">Dew expected</span>
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Temp °C</span>
                <input type="number" value={weatherTempC} onChange={(e) => setWeatherTempC(e.target.value)}
                  className="bg-bg-secondary text-text-primary px-3 py-2 rounded" placeholder="e.g. 28" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Wind kph</span>
                <input type="number" value={weatherWindKph} onChange={(e) => setWeatherWindKph(e.target.value)}
                  className="bg-bg-secondary text-text-primary px-3 py-2 rounded" placeholder="e.g. 12" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Humidity %</span>
                <input type="number" value={weatherHumidity} onChange={(e) => setWeatherHumidity(e.target.value)}
                  className="bg-bg-secondary text-text-primary px-3 py-2 rounded" placeholder="e.g. 75" />
              </label>
              <label className="flex flex-col gap-1 md:col-span-3">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Pitch report (curator's notes)</span>
                <textarea value={pitchReport} onChange={(e) => setPitchReport(e.target.value)}
                  className="bg-bg-secondary text-text-primary px-3 py-2 rounded" rows={2}
                  placeholder="e.g. red soil, hard surface, expected to take spin from day 3…" />
              </label>
            </div>
          </div>

          {error && (
            <div className="mb-3 px-3 py-2 rounded bg-rose-500/15 text-rose-300 border border-rose-500/40 text-sm flex items-center justify-between gap-3">
              <span>⚠ {error}</span>
              <button
                type="button"
                onClick={() => setError("")}
                className="text-rose-200 hover:text-white px-1"
                aria-label="Dismiss"
              >×</button>
            </div>
          )}

          {/* Save Button */}
          <button
            id="save-toss-btn"
            disabled={!canSave}
            onClick={saveToss}
            className="w-full py-4 text-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{
              backgroundColor: canSave ? "#6B1991" : "#23232F",
              color: canSave ? "#FFFFFF" : "#CFCFD1",
            }}
          >
            {saving ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving Toss...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Save Toss & Continue
              </>
            )}
          </button>
        </div>
      </div>

      {/* READY Modal */}
      <Modal
        open={showReadyModal}
        title="Mark match as ready?"
        body="The match must be in READY state before you can record the toss. Click Confirm to proceed, or Cancel to go back to the match list."
        confirmLabel={updatingState ? "Updating..." : "Confirm"}
        confirmDestructive={false}
        confirmDisabled={updatingState}
        onConfirm={async () => {
          setUpdatingState(true);
          try {
            await api.post(`/api/matches/matches/${match.id}/state/`, {
              state: "READY",
            });
            setMatch((prev) => ({ ...prev, state: "READY" }));
            setShowReadyModal(false);
          } catch (err) {
            alert(formatError(err, "Failed to update match state"));
          } finally {
            setUpdatingState(false);
          }
        }}
        onCancel={() => navigate("/cricket/matches")}
      />
    </div>
  );
}
